import _ from 'lodash'

import {
  battery_yearly_cost_index,
  beb_yearly_maintenance_cost_index,
  diesel_yearly_maintenance_cost_index,
  residual_battery_yearly_cost_index,
} from './cost_index'

import { auxiliary_load_option_data } from './hv_options'

export class VehicleCalculation {
  setting_input: any
  vehicle_input: any
  region_data_input: any
  local_vehicle_data: any

  // variables
  cost_per_litre: number
  co2_per_litre: number
  currentYear: any

  result: any

  constructor(
    setting_input: any,
    vehicle_input: any,
    region_data_input: any,
    local_vehicle_data: any,
  ) {
    // input
    this.setting_input = setting_input // settings input
    this.vehicle_input = vehicle_input // vehicle setting from first page + ev vehicle

    // region data
    this.region_data_input = region_data_input // region data (response from region api)
    this.local_vehicle_data = local_vehicle_data

    // static variables
    this.cost_per_litre = this.region_data_input?.fuel_diesel_price
    this.co2_per_litre = 2887
    this.currentYear = new Date().getFullYear()

    this.result = {}
  }

  get_diesel_vehicle() {
    const diesel_bus_id = this.vehicle_input?.ice_chassis
    const res =
      diesel_bus_id &&
      this.local_vehicle_data &&
      _.find(this.local_vehicle_data, { id: diesel_bus_id })
    this.result['diesel_bus'] = res
    return res
  }

  // checked calculations

  get_cost_per_kwh() {
    const { off_peak_rate, shoulder_rate, peak_rate, ev_charging } = this.setting_input
    const age_rate1 = ev_charging && ev_charging[0]
    const age_rate2 = ev_charging && ev_charging[1] - age_rate1
    const age_rate3 = 100 - (age_rate1 + age_rate2)

    const cost_rate_1 = peak_rate
    const cost_rate_2 = off_peak_rate
    const cost_rate_3 = shoulder_rate

    const res =
      (age_rate1 / 100) * cost_rate_1 +
      (age_rate2 / 100) * cost_rate_2 +
      (age_rate3 / 100) * cost_rate_3
    this.result['cost_per_kwh'] = {
      age_rate1,
      age_rate2,
      age_rate3,
      cost_rate_1,
      cost_rate_2,
      cost_rate_3,
      cost_per_kwh: res,
    }
    return res
  }

  get_co2_per_kwh() {
    this.result['electricity_grid_carbon'] = this.region_data_input?.electricity_grid_carbon
    this.result['region_data_input'] = this.region_data_input
    const res = this.region_data_input?.electricity_grid_carbon

    return res
  }

  get_fuel_efficience() {
    const auxiliary_load = this.vehicle_input?.auxiliary_load

    const res = auxiliary_load ? _.get(auxiliary_load_option_data, auxiliary_load) : {}
    this.result['auxiliary_load'] = res
    return res
  }

  get_kwh_per_km() {
    const { terrain, vehicle_payload, efficiency } = this.vehicle_input

    const auxiliary_load_efficiency = this.get_fuel_efficience()

    let result = 1

    if (terrain === 'Flat') {
      const kwhPerKm = efficiency * (0.95 + ((vehicle_payload - 1625) * 0.05) / 650)
      result = kwhPerKm + auxiliary_load_efficiency?.['kwh_km']
    }

    if (terrain === 'Moderate') {
      const kwhPerKm = efficiency * (1.05 + ((vehicle_payload - 1625) * 0.05) / 650)
      result = kwhPerKm + auxiliary_load_efficiency?.['kwh_km']
    }
    if (terrain === 'Steep') {
      const kwhPerKm = efficiency * (1.1 + ((vehicle_payload - 1625) * 0.05) / 650)
      result = kwhPerKm + auxiliary_load_efficiency?.['kwh_km']
    }
    this.result['kwh_per_km'] = result
    return result
  }

  get_litres_per_km() {
    const { terrain, vehicle_payload } = this.vehicle_input
    const vehicle_efficiency = this.get_diesel_vehicle()?.['efficiency']
    // const vehicle_efficiency = vehicle.efficiency

    const auxiliary_load_efficiency = this.get_fuel_efficience()

    let result = 1

    if (terrain === 'Flat') {
      const litresPerKm = vehicle_efficiency * (0.95 + ((vehicle_payload - 1625) * 0.05) / 650)
      result = litresPerKm + auxiliary_load_efficiency['l_km']
    }

    if (terrain === 'Moderate') {
      const litresPerKm = vehicle_efficiency * (1.05 + ((vehicle_payload - 1625) * 0.05) / 650)
      result = litresPerKm + auxiliary_load_efficiency['l_km']
    }
    if (terrain === 'Steep') {
      const litresPerKm = vehicle_efficiency * (1.1 + ((vehicle_payload - 1625) * 0.05) / 650)
      result = litresPerKm + auxiliary_load_efficiency['l_km']
    }
    this.result['litres_per_km'] = result
    return result
  }

  get_km_driven() {
    const { distance_travelled_per_day, working_days } = this.vehicle_input
    const no_of_days_per_week = working_days && working_days.length
    const no_of_weeks = 50
    const res = distance_travelled_per_day * no_of_days_per_week * no_of_weeks
    this.result['km_driven'] = res
    return res
  }

  get_diesel_depreciation() {
    // checked and working
    const vehicle_cost = this.get_diesel_vehicle()?.chassis_cost

    const { body_coast } = this.vehicle_input
    const { diesel_bus_yearly_depreciation, operating_period } = this.setting_input // todo change bus to vehivle
    const total_ice_purchase_price = _.toNumber(vehicle_cost) + body_coast

    const deprecation = diesel_bus_yearly_depreciation / 100

    const res =
      total_ice_purchase_price - total_ice_purchase_price * (1 - deprecation) ** operating_period

    this.result['diesel_depreciation'] = res

    return res ? _.round(res) : 0
  }

  get_electric_depreciation() {
    // checked and working
    const { body_coast, chassis_cost: vehicle_cost } = this.vehicle_input

    const { electric_bus_yearly_depreciation, operating_period } = this.setting_input
    const deprecation = electric_bus_yearly_depreciation / 100 // todo change bus to vehivle
    const total_ice_purchase_price = _.toNumber(vehicle_cost) + body_coast

    const res =
      total_ice_purchase_price - total_ice_purchase_price * (1 - deprecation) ** operating_period

    this.result['electric_depreciation'] = res

    return res ? _.round(res) : 0
  }

  get_diesel_maintenance() {
    const km_driven = this.get_km_driven()

    const { operating_period } = this.setting_input

    // const { service_cost_km, tyre_cost_km } = this.get_diesel_vehicle()
    const tyre_cost_km = this.get_diesel_vehicle()?.tyre_cost_km
    const service_cost_km = this.get_diesel_vehicle()?.service_cost_km

    const diesel_bus_maintenance_cost_pounds_km =
      tyre_cost_km && service_cost_km
        ? _.toNumber(tyre_cost_km) + _.toNumber(service_cost_km)
        : 0.07

    const res = this.calc_diesel_maintenance_cost(
      km_driven,
      diesel_bus_maintenance_cost_pounds_km,
      operating_period,
    )

    this.result['diesel_maintenance'] = res

    return _.round(res)
  }

  get_electric_maintenance() {
    const { operating_period } = this.setting_input
    const km_driven = this.get_km_driven()

    const { tyre_cost_km, service_cost_km } = this.vehicle_input

    const beb_bus_maintenance_cost_pounds_km =
      tyre_cost_km && service_cost_km
        ? _.toNumber(tyre_cost_km) + _.toNumber(service_cost_km)
        : 0.03

    const res = this.calc_electricity_maintenance_cost(
      km_driven,
      beb_bus_maintenance_cost_pounds_km,
      operating_period,
    )
    this.result['electric_maintenance'] = res
    return res
  }

  get_electric_battery_cost() {
    //calculation required (bus battery cost / kWh * bus battery size * battery yearly cost index) use the cost index based on the year when the battery is replaced
    // calculation required (vehicle battery cost / kWh * vehicle battery size * battery yearly cost index) use the cost index based on the year when the battery is replaced
    const bus_battery_size = this.vehicle_input?.battery

    const battery_replacement_period = this.setting_input?.battery_replacement_period || 1

    const bus_battery_cost_kWh = 203
    const { operating_period } = this.setting_input

    const res = this.cal_electric_battery_cost(
      bus_battery_cost_kWh,
      bus_battery_size,
      battery_yearly_cost_index,
      operating_period,
      battery_replacement_period,
    )
    this.result['electric_battery_cost'] = res
    return res
  }

  get_battery_depreciation() {
    //calculation required (residual bus battery cost / kWh * bus battery size * residual battery yearly cost index) use the cost index based on the year when the battery is replaced
    const residual_bus_battery_cost = 41
    const bus_battery_size = this.vehicle_input?.battery

    const battery_replacement_period = this.setting_input?.battery_replacement_period || 1
    const { operating_period } = this.setting_input

    const res = this.calc_battery_deprecation(
      residual_bus_battery_cost,
      bus_battery_size,
      residual_battery_yearly_cost_index,
      battery_replacement_period,
      operating_period,
    )
    this.result['battery_depreciation'] = res
    return res
  }

  get_battery_replacement() {
    //formula electric_battery_cost - battery_depreciation

    const res = this.get_electric_battery_cost() - this.get_battery_depreciation()
    this.result['battery_replacement'] = res
    return res
  }

  get_co2_diesel() {
    //formula km_driven * litres_per_km * co2_per_litre
    const { operating_period } = this.setting_input
    const km_driven = this.get_km_driven()
    const litres_per_km = this.get_litres_per_km()
    const co2_per_litre = this.co2_per_litre

    const res = km_driven * litres_per_km * co2_per_litre * operating_period
    this.result['co2_diesel'] = res
    return res
  }

  get_fuel_cost_diesel_per_year() {
    //formula (km_driven * litres_per_km * cost_per_litre) - Diesel bus grant
    const km_driven = this.get_km_driven()
    const litres_per_km = this.get_litres_per_km()
    const cost_per_litre = this.setting_input?.price_of_diesel

    const res = km_driven * litres_per_km * cost_per_litre

    this.result['fuel_cost_diesel_per_year'] = {
      km_driven,
      litres_per_km,
      cost_per_litre,
      res: res,
    }
    return res
  }

  get_green_power() {
    const { electricity_green_percent } = this.setting_input
    const res = electricity_green_percent / 100
    this.result['green_power'] = res

    return res
  }

  get_co2_electric() {
    const { operating_period } = this.setting_input

    const km_driven = this.get_km_driven()
    const kwh_per_km = this.get_kwh_per_km()
    const co2_per_kwh = this.get_co2_per_kwh()

    const green_power = this.get_green_power()

    const res = km_driven * kwh_per_km * co2_per_kwh * operating_period * (1 - green_power)

    this.result['co2_electric_base'] = res
    return res
  }

  get_fuel_cost_electric_per_year() {
    //(km_driven * kwh_per_km * cost_per_kwh) - Electric bus grant
    const km_driven = this.get_km_driven()
    const kwh_per_km = this.get_kwh_per_km()
    const cost_per_kwh = this.get_cost_per_kwh()

    const res = km_driven * kwh_per_km * cost_per_kwh
    this.result['fuel_cost_electric_per_year_dt'] = {
      km_driven,
      kwh_per_km,
      cost_per_kwh,
    }
    this.result['fuel_cost_electric_per_year'] = res

    return res
  }

  get_fuel_cost_diesel() {
    const { operating_period } = this.setting_input

    const fuel_cost_diesel_per_year = this.get_fuel_cost_diesel_per_year()

    const res = fuel_cost_diesel_per_year * operating_period

    this.result['fuel_cost_diesel'] = res
    return res
  }

  get_fuel_cost_electric() {
    const { operating_period } = this.setting_input

    const fuel_cost_electric_per_year = this.get_fuel_cost_electric_per_year()

    const res = fuel_cost_electric_per_year * operating_period
    this.result['fuel_cost_electric'] = res
    return res
  }

  // results

  get_tco_diesel() {
    //formula diesel_depreciation + diesel_maintenance + fuel_cost_diesel for the entire holding period
    const diesel_deprecation = this.get_diesel_depreciation()
    const diesel_maintenance = this.get_diesel_maintenance()
    const fuel_diesel = this.get_fuel_cost_diesel()
    const res = diesel_maintenance + diesel_deprecation + fuel_diesel

    this.result['tco_diesel'] = res
    return res
  }

  get_tco_electric() {
    // electric_depreciation + electric_maintenance + fuel_cost_electric + electric battery cost - battery_depreciation for the entire holding period
    //todo electric_depreciation + electric_maintenance + fuel_cost_electric + electric battery cost - battery_depreciation for the entire holding period
    const electric_depreciation = this.get_electric_depreciation()

    const electric_maintenance = this.get_electric_maintenance()

    const fuel_cost_electric = this.get_fuel_cost_electric()

    const electric_battery_cost = this.get_electric_battery_cost()

    const battery_depreciation = this.get_battery_depreciation()

    const res =
      electric_depreciation +
      electric_maintenance +
      fuel_cost_electric +
      electric_battery_cost -
      battery_depreciation

    this.result['tco_electric'] = res

    return res
  }

  get_total_cost_saving() {
    //formula TCO_diesel - TCO_electric
    const tco_diesel = this.get_tco_diesel()
    const tco_electric = this.get_tco_electric()

    const res = tco_diesel - tco_electric

    this.result['total_cost_saving'] = res
    return _.round(res)
  }

  get_total_emissions_reduced() {
    //formula co2_diesel - co2_electric
    const co2_diesel = this.get_co2_diesel()
    const co2_electric = this.get_co2_electric()
    // const { operating_period } = this.setting_input

    const result = co2_diesel - co2_electric
    this.result['total_cost_saving'] = result

    return _.round(result)
  }

  get_diesel_vehicle_range() {
    const litres_per_km = this.get_litres_per_km()

    const battery_range = this.get_diesel_vehicle()?.battery

    const result = parseInt(battery_range) / litres_per_km

    this.result['diesel_vehicle_range'] = result

    return _.round(result)
  }

  get_ev_vehicle_range() {
    const kwh_per_km = this.get_kwh_per_km()
    const battery_range = this.vehicle_input.battery
    const result = parseInt(battery_range) / kwh_per_km

    this.result['ev_vehicle_range'] = result

    return _.round(result)
  }

  // calc costst
  calc_diesel_maintenance_cost = (
    km_driven: any,
    diesel_bus_maintenance_cost_pounds_km: any,
    operating_period: any,
  ) => {
    let sum = 0

    for (let i = 1; i <= operating_period; i++) {
      const bebValue =
        diesel_yearly_maintenance_cost_index[Object.keys(diesel_yearly_maintenance_cost_index)[i]]
      const formula = diesel_bus_maintenance_cost_pounds_km * km_driven * bebValue
      sum += formula
    }
    return sum
  }

  calc_electricity_maintenance_cost = (
    km_driven: any,
    beb_bus_maintenance_cost_pounds_km: any,
    operating_period: any,
  ) => {
    let sum = 0

    for (let i = 1; i <= operating_period; i++) {
      const bebValue =
        beb_yearly_maintenance_cost_index[Object.keys(beb_yearly_maintenance_cost_index)[i]]
      const formula = beb_bus_maintenance_cost_pounds_km * km_driven * bebValue
      sum += formula
    }

    return sum
  }

  cal_electric_battery_cost = (
    bus_battery_cost_kWh: any,
    bus_battery_size: any,
    battery_yearly_cost_index: any,
    operating_period: any,
    battery_replacement_period: any,
  ) => {
    const parseObj = Object.keys(battery_yearly_cost_index)
    const sliceArr = parseObj.slice(0, operating_period)
    const dataLength = Object.keys(sliceArr).length

    const arr = []

    for (let i = battery_replacement_period; i <= dataLength; i += battery_replacement_period) {
      arr.push(battery_yearly_cost_index[Object.keys(battery_yearly_cost_index)[i]])
    }

    const newArr = _.without(arr, undefined, null, NaN)

    let sum = 0

    for (let i = 0; i <= newArr.length - 1; i++) {
      sum += +bus_battery_cost_kWh * bus_battery_size * newArr[i]
    }

    return sum
  }

  calc_battery_deprecation = (
    residual_bus_battery_cost: any,
    bus_battery_size: any,
    residual_battery_yearly_cost_index: any,
    battery_replacement_period: any,
    operating_period: any,
  ) => {
    const parseObj = Object.keys(battery_yearly_cost_index)
    const sliceArr = parseObj.slice(0, operating_period)
    const dataLength = Object.keys(sliceArr).length

    const arr = []

    for (let i = battery_replacement_period; i <= dataLength; i += battery_replacement_period) {
      arr.push(battery_yearly_cost_index[Object.keys(battery_yearly_cost_index)[i]])
    }

    const newArr = _.without(arr, undefined, null, NaN)

    let sum = 0

    for (let i = 0; i <= newArr.length - 1; i++) {
      sum += +residual_bus_battery_cost * bus_battery_size * newArr[i]
    }

    return sum
  }
}
